import { lazy, Suspense, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { Capacitor } from "@capacitor/core";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProfileProvider } from "@/contexts/ProfileContext";
import { PublishProvider } from "@/contexts/PublishContext";
import { FeatureFlagProvider, useFeatureFlag } from "@/contexts/FeatureFlagContext";
import { OtaUpdateProvider, useOtaUpdate } from "@/contexts/OtaUpdateContext";
import PublishProgress from "@/components/publish/PublishProgress";
import SciFiLoader from "@/components/ui/SciFiLoader";
import MainLayout from "@/components/layout/MainLayout";
import { usePageViewTracker } from "@/hooks/use-page-view-tracker";
import { useNativeAppSetup, useNativeBackButton, useNativeDeepLinks, NATIVE_POST_AUTH_PENDING_KEY } from "@/hooks/use-native-app";
import { usePushNotifications } from "@/hooks/use-push-notifications";
import ForceUpdateDialog from "@/components/ForceUpdateDialog";
import ErrorFallback from "@/components/ErrorFallback";
import PostAuthRedirect from "@/components/auth/PostAuthRedirect";
import { resolvePostAuthRoute } from "@/lib/post-auth-route";
import { getResolvedRoute, setResolvedRoute, clearAllResolvedRoutes } from "@/lib/resolved-route-cache";
// NativePermissionsDialog removed — Apple Guideline 5.1.1(iv) compliance
// Permissions are now requested just-in-time when the user needs them

// Lazy-load all page components for code splitting
const Login = lazy(() => import("./pages/Login"));
const Home = lazy(() => import("./pages/Home"));
const CreatePost = lazy(() => import("./pages/CreatePost"));
const PostDetail = lazy(() => import("./pages/PostDetail"));
const DailyFortune = lazy(() => import("./pages/DailyFortune"));
const AstroProfile = lazy(() => import("./pages/AstroProfile"));
const AstroProfileList = lazy(() => import("./pages/AstroProfileList"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Search = lazy(() => import("./pages/Search"));
const Discover = lazy(() => import("./pages/Discover"));
const VideoFeed = lazy(() => import("./pages/VideoFeed"));
const Profile = lazy(() => import("./pages/Profile"));
const MyPosts = lazy(() => import("./pages/MyPosts"));
const LikesAndBookmarks = lazy(() => import("./pages/LikesAndBookmarks"));
const BrowseHistory = lazy(() => import("./pages/BrowseHistory"));
const SocialConnections = lazy(() => import("./pages/SocialConnections"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const Invitation = lazy(() => import("./pages/Invitation"));
const Points = lazy(() => import("./pages/Points"));
const Messages = lazy(() => import("./pages/Messages"));
const MyLikers = lazy(() => import("./pages/MyLikers"));
const AiTarot = lazy(() => import("./pages/AiTarot"));
const AiAstrology = lazy(() => import("./pages/AiAstrology"));
const AiQuery = lazy(() => import("./pages/AiQuery"));
const AiTravel = lazy(() => import("./pages/AiTravel"));
const AiBazi = lazy(() => import("./pages/AiBazi"));
const AiZiwei = lazy(() => import("./pages/AiZiwei"));
const AiHistory = lazy(() => import("./pages/AiHistory"));
const AiHistoryDetail = lazy(() => import("./pages/AiHistoryDetail"));
const SplashPreview = lazy(() => import("./pages/SplashPreview"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,   // 2 minutes default
      gcTime: 10 * 60 * 1000,     // 10 minutes garbage collection
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false,       // avoid redundant refetch on tab switch
    },
  },
});

const PageLoader = () => <SciFiLoader fullscreen />;

/**
 * Wrapper that resolves onboarding status BEFORE rendering Home.
 * This is the SINGLE source of truth for "new user → /onboarding" redirect,
 * eliminating the race between Home.tsx and PostAuthRedirect.
 */
// Cache resolved onboarding status per user so subsequent navigations to "/"
// don't re-fetch and flash a loader.  Uses shared cache from resolved-route-cache.ts
// so Onboarding.tsx can clear it after completion.

const ResolvedHomeRoute = () => {
  const { user } = useAuth();
  const location = useLocation();
  const cachedTarget = user ? getResolvedRoute(user.id) : undefined;
  const [resolved, setResolved] = useState<{ target: string } | null>(
    cachedTarget !== undefined ? { target: cachedTarget } : null
  );

  useEffect(() => {
    if (!user) return;
    // Already resolved for this user — skip
    const cached = getResolvedRoute(user.id);
    if (cached !== undefined) {
      setResolved((prev) => (prev?.target === cached ? prev : { target: cached }));
      return;
    }

    let active = true;
    setResolved(null);

    resolvePostAuthRoute(user.id, new URLSearchParams(location.search).get('invite') || '')
      .then((r) => {
        if (!active) return;
        setResolvedRoute(user.id, r.target);
        setResolved(r);
      })
      .catch(() => {
        if (!active) return;
        setResolvedRoute(user.id, '/onboarding');
        setResolved({ target: '/onboarding' });
      });

    return () => { active = false; };
  }, [user?.id]);

  if (!user || !resolved) return <PageLoader />;
  if (resolved.target !== '/') return <Navigate to={resolved.target} replace />;
  return <Home />;
};

const isNativePostAuthPending = () => {
  try {
    return sessionStorage.getItem(NATIVE_POST_AUTH_PENDING_KEY) === '1';
  } catch {
    return false;
  }
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (isNativePostAuthPending() && location.pathname !== '/login') {
    return <PageLoader />;
  }
  return <>{children}</>;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader />;
  if (user && location.pathname === '/login') {
    return <PostAuthRedirect userId={user.id} />;
  }
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
};

/** Invisible component that tracks page views and sets up native features — placed inside BrowserRouter + AuthProvider */
const AppServices = () => {
  usePageViewTracker();
  useNativeAppSetup();
  useNativeBackButton();
  useNativeDeepLinks();
  usePushNotifications();

  // Clear WebView cache after OTA update to prevent stale CSS/JS
  useEffect(() => {
    const clearOtaCaches = async () => {
      try {
        const flag = localStorage.getItem('ota_clear_cache');
        if (flag !== '1') return;

        // Remove flag FIRST to prevent reload loops
        localStorage.removeItem('ota_clear_cache');

        // Skip if already cleared this session
        if (sessionStorage.getItem('ota_cache_cleared') === '1') return;
        sessionStorage.setItem('ota_cache_cleared', '1');

        if ('caches' in window) {
          const names = await caches.keys();
          await Promise.all(names.map((name) => caches.delete(name)));
        }

        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map((r) => r.unregister()));
        }

        console.log('[OTA] Cleared WebView caches after update');
      } catch {}
    };

    void clearOtaCaches();
  }, []);

  // Show success banner if app was just OTA-updated
  const { setOtaStatus } = useOtaUpdate();

  useEffect(() => {
    try {
      const updatedVersion = localStorage.getItem('ota_updated_version');
      if (updatedVersion) {
        localStorage.removeItem('ota_updated_version');
        localStorage.removeItem('ota_release_notes');
        setOtaStatus('success', updatedVersion);
        // Clear resolved route cache after OTA reload so fresh onboarding
        // status is re-fetched (prevents stale redirect loops)
        clearAllResolvedRoutes();
      }
    } catch {}
  }, [setOtaStatus]);

  // Confirm the current OTA bundle only after the app stays stable for a while,
  // then check for the next update.
  useEffect(() => {
    // Skip OTA work if we just applied an update and cache cleanup is still pending
    try {
      if (localStorage.getItem('ota_clear_cache') === '1') return;
    } catch {}

    let cancelled = false;
    const delayMs = Capacitor.getPlatform() === 'android' ? 8000 : 5000;
    const timer = window.setTimeout(() => {
      import('@/lib/ota-updater').then(async ({ notifyOtaReady, checkAndApplyOtaUpdate, resetOtaRetryCounter }) => {
        if (cancelled) return;

        resetOtaRetryCounter();
        await notifyOtaReady().catch(() => {});
        if (cancelled) return;

        checkAndApplyOtaUpdate((status, version) => {
          setOtaStatus(status as any, version);
        }).catch(() => {});
      }).catch(() => {});
    }, delayMs);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [setOtaStatus]);

  // Re-check OTA when app returns from background
  useEffect(() => {
    const handleResume = () => {
      try {
        if (localStorage.getItem('ota_clear_cache') === '1') return;
      } catch {}
      import('@/lib/ota-updater').then(({ checkAndApplyOtaUpdate, resetOtaRetryCounter }) => {
        resetOtaRetryCounter();
        checkAndApplyOtaUpdate((status, version) => {
          setOtaStatus(status as any, version);
        });
      }).catch(() => {});
    };

    let removeListener: (() => void) | undefined;

    import('@capacitor/app').then(({ App: CapApp }) => {
      CapApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) handleResume();
      }).then(listener => {
        removeListener = () => listener.remove();
      });
    }).catch(() => {});

    return () => { removeListener?.(); };
  }, [setOtaStatus]);

  return null;
};

/** Feature-flag-aware route guard — redirects to home if flag is off */
const FlagRoute = ({ flag, children }: { flag: string; children: React.ReactNode }) => {
  const { isEnabled } = useFeatureFlag();
  if (!isEnabled(flag)) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const AppRoutes = () => {
  const { isEnabled } = useFeatureFlag();

  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />

      {/* Main tab routes - with BottomNav */}
      <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
        <Route path="/" element={<ResolvedHomeRoute />} />
        {isEnabled('search') && <Route path="/search" element={<Search />} />}
        {isEnabled('discover') && <Route path="/discover" element={<Discover />} />}
        <Route path="/messages" element={<Messages />} />
        <Route path="/profile" element={<Profile />} />
      </Route>

      {/* Other routes - no BottomNav */}
      <Route path="/post/:id" element={<ProtectedRoute><PostDetail /></ProtectedRoute>} />
      <Route path="/video/:id" element={<ProtectedRoute><FlagRoute flag="video_feed"><VideoFeed /></FlagRoute></ProtectedRoute>} />
      <Route path="/create" element={<ProtectedRoute><FlagRoute flag="create_post"><CreatePost /></FlagRoute></ProtectedRoute>} />
      <Route path="/daily-fortune" element={<ProtectedRoute><FlagRoute flag="daily_fortune"><DailyFortune /></FlagRoute></ProtectedRoute>} />
      <Route path="/astro-profile" element={<ProtectedRoute><FlagRoute flag="astro_profiles"><AstroProfileList /></FlagRoute></ProtectedRoute>} />
      <Route path="/astro-profile/:id" element={<ProtectedRoute><FlagRoute flag="astro_profiles"><AstroProfile /></FlagRoute></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/my-posts" element={<ProtectedRoute><MyPosts /></ProtectedRoute>} />
      <Route path="/likes-bookmarks" element={<ProtectedRoute><LikesAndBookmarks /></ProtectedRoute>} />
      <Route path="/browse-history" element={<ProtectedRoute><FlagRoute flag="browse_history"><BrowseHistory /></FlagRoute></ProtectedRoute>} />
      <Route path="/social" element={<ProtectedRoute><FlagRoute flag="social_follow"><SocialConnections /></FlagRoute></ProtectedRoute>} />
      <Route path="/edit-profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
      <Route path="/user/:userId" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
      <Route path="/invitation" element={<ProtectedRoute><FlagRoute flag="invitation"><Invitation /></FlagRoute></ProtectedRoute>} />
      <Route path="/points" element={<ProtectedRoute><Points /></ProtectedRoute>} />
      <Route path="/my-likers" element={<ProtectedRoute><MyLikers /></ProtectedRoute>} />
      <Route path="/ai/tarot" element={<ProtectedRoute><FlagRoute flag="ai_tarot"><AiTarot /></FlagRoute></ProtectedRoute>} />
      <Route path="/ai/astrology" element={<ProtectedRoute><FlagRoute flag="ai_astrology"><AiAstrology /></FlagRoute></ProtectedRoute>} />
      <Route path="/ai/query" element={<ProtectedRoute><FlagRoute flag="ai_query"><AiQuery /></FlagRoute></ProtectedRoute>} />
      <Route path="/ai/travel" element={<ProtectedRoute><FlagRoute flag="ai_travel"><AiTravel /></FlagRoute></ProtectedRoute>} />
      <Route path="/ai/bazi" element={<ProtectedRoute><FlagRoute flag="ai_bazi"><AiBazi /></FlagRoute></ProtectedRoute>} />
      <Route path="/ai/ziwei" element={<ProtectedRoute><FlagRoute flag="ai_ziwei"><AiZiwei /></FlagRoute></ProtectedRoute>} />
      <Route path="/ai/history" element={<ProtectedRoute><AiHistory /></ProtectedRoute>} />
      <Route path="/ai/history/:id" element={<ProtectedRoute><AiHistoryDetail /></ProtectedRoute>} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/auth-callback" element={<AuthCallback />} />
      <Route path="/splash-preview" element={<SplashPreview />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <Sentry.ErrorBoundary fallback={({ error, resetError }) => <ErrorFallback error={error instanceof Error ? error : new Error(String(error))} resetError={resetError} />}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <ProfileProvider>
            <PublishProvider>
            <FeatureFlagProvider>
            <OtaUpdateProvider>
              <PublishProgress />
              <ForceUpdateDialog />
              <AppServices />
              
              <Suspense fallback={<PageLoader />}>
                <AppRoutes />
              </Suspense>
            </OtaUpdateProvider>
            </FeatureFlagProvider>
            </PublishProvider>
            </ProfileProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
